import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Shield, Users, Building, CheckCircle, XCircle, WarningCircle } from '@phosphor-icons/react';

export default function AccountMigration() {
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [results, setResults] = useState(null);
  const [adminCheck, setAdminCheck] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = [];
      usersSnapshot.forEach((doc) => {
        usersData.push({ id: doc.id, ...doc.data() });
      });
      setUsers(usersData);

      // Load accounts
      const accountsSnapshot = await getDocs(collection(db, 'accounts'));
      const accountsData = [];
      accountsSnapshot.forEach((doc) => {
        accountsData.push({ id: doc.id, ...doc.data() });
      });
      setAccounts(accountsData);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    try {
      setMigrating(true);
      setResults(null);

      let accountsCreated = 0;
      let usersUpdated = 0;
      let skipped = 0;
      const errors = [];

      for (const user of users) {
        // Check if user already has an accountId
        if (user.accountId && user.accountId !== 'null' && user.accountId !== '') {
          skipped++;
          continue;
        }

        try {
          // Create account for the user
          const accountData = {
            name: user.name || user.displayName || user.email,
            description: `Account for ${user.name || user.displayName || user.email}`,
            contactEmail: user.email,
            status: 'active',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          const accountRef = await addDoc(collection(db, 'accounts'), accountData);
          console.log(`Created account: ${accountRef.id}`);

          // Update user with account information
          await updateDoc(doc(db, 'users', user.id), {
            accountId: accountRef.id,
            accountName: accountData.name,
            updatedAt: new Date().toISOString()
          });

          console.log(`Updated user ${user.email} with account info`);

          accountsCreated++;
          usersUpdated++;

        } catch (error) {
          console.error(`Error processing user ${user.email}:`, error);
          errors.push({ user: user.email, error: error.message });
        }
      }

      setResults({
        accountsCreated,
        usersUpdated,
        skipped,
        errors
      });

      // Reload data to show updated results
      await loadData();

    } catch (error) {
      console.error('Migration error:', error);
      setResults({
        accountsCreated: 0,
        usersUpdated: 0,
        skipped: 0,
        errors: [{ user: 'System', error: error.message }]
      });
    } finally {
      setMigrating(false);
    }
  };

  const checkAdminRoles = async () => {
    try {
      setAdminCheck(null);
      
      const adminUsers = users.filter(user => user.role === 'ADMIN');
      const nonAdminUsers = users.filter(user => user.role !== 'ADMIN');
      
      setAdminCheck({
        totalUsers: users.length,
        adminUsers: adminUsers.length,
        nonAdminUsers: nonAdminUsers.length,
        adminList: adminUsers,
        nonAdminList: nonAdminUsers
      });
      
    } catch (error) {
      console.error('Error checking admin roles:', error);
      setAdminCheck({
        error: error.message
      });
    }
  };

  const fixAdminRoles = async () => {
    try {
      setMigrating(true);
      setResults(null);
      
      let fixedRoles = 0;
      const errors = [];
      
      // List of emails that should be admins (you can modify this list)
      const adminEmails = [
        'eric@lithoco.com',
        // Add other admin emails here
      ];
      
      for (const user of users) {
        if (adminEmails.includes(user.email) && user.role !== 'ADMIN') {
          try {
            await updateDoc(doc(db, 'users', user.id), {
              role: 'ADMIN',
              updatedAt: new Date().toISOString()
            });
            
            console.log(`Updated ${user.email} to ADMIN role`);
            fixedRoles++;
            
          } catch (error) {
            console.error(`Error updating role for ${user.email}:`, error);
            errors.push({ user: user.email, error: error.message });
          }
        }
      }
      
      setResults({
        accountsCreated: 0,
        usersUpdated: fixedRoles,
        skipped: 0,
        errors,
        message: `Fixed ${fixedRoles} admin roles`
      });
      
      // Reload data to show updated results
      await loadData();
      await checkAdminRoles();
      
    } catch (error) {
      console.error('Error fixing admin roles:', error);
      setResults({
        accountsCreated: 0,
        usersUpdated: 0,
        skipped: 0,
        errors: [{ user: 'System', error: error.message }]
      });
    } finally {
      setMigrating(false);
    }
  };

  const usersWithoutAccounts = users.filter(user => 
    !user.accountId || user.accountId === 'null' || user.accountId === ''
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading migration data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Account Migration Tool</h1>
            </div>
            <p className="mt-2 text-gray-600">
              Create accounts for users who don't have them. This fixes the issue where Google sign-in users and older users don't have account records.
            </p>
          </div>

          <div className="p-6">
            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Total Users</p>
                    <p className="text-2xl font-bold text-blue-900">{users.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Building className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Total Accounts</p>
                    <p className="text-2xl font-bold text-green-900">{accounts.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <WarningCircle className="h-8 w-8 text-orange-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">Users Without Accounts</p>
                    <p className="text-2xl font-bold text-orange-900">{usersWithoutAccounts.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mb-8 space-y-4">
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={runMigration}
                  disabled={migrating || usersWithoutAccounts.length === 0}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    migrating || usersWithoutAccounts.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {migrating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Running Migration...
                    </div>
                  ) : (
                    `Create ${usersWithoutAccounts.length} Missing Accounts`
                  )}
                </button>

                <button
                  onClick={checkAdminRoles}
                  disabled={migrating}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Check Admin Roles
                </button>

                <button
                  onClick={fixAdminRoles}
                  disabled={migrating}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Fix Admin Roles
                </button>
              </div>
            </div>

            {/* Admin Role Check Results */}
            {adminCheck && (
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Role Check Results</h3>
                
                {adminCheck.error ? (
                  <div className="text-red-600">
                    <p><strong>Error:</strong> {adminCheck.error}</p>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{adminCheck.totalUsers}</p>
                        <p className="text-sm text-gray-600">Total Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{adminCheck.adminUsers}</p>
                        <p className="text-sm text-gray-600">Admin Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-orange-600">{adminCheck.nonAdminUsers}</p>
                        <p className="text-sm text-gray-600">Non-Admin Users</p>
                      </div>
                    </div>

                    {adminCheck.adminList.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-green-800 mb-2">Admin Users:</h4>
                        <div className="bg-green-50 p-3 rounded">
                          {adminCheck.adminList.map((user, index) => (
                            <p key={index} className="text-sm text-green-700">
                              {user.email} ({user.name || 'No name'})
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {adminCheck.nonAdminList.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-orange-800 mb-2">Non-Admin Users:</h4>
                        <div className="bg-orange-50 p-3 rounded">
                          {adminCheck.nonAdminList.map((user, index) => (
                            <p key={index} className="text-sm text-orange-700">
                              {user.email} ({user.name || 'No name'}) - Role: {user.role || 'NO ROLE'}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Results */}
            {results && (
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Migration Results</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Accounts Created</p>
                    <p className="text-2xl font-bold text-green-600">{results.accountsCreated}</p>
                  </div>
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Users Updated</p>
                    <p className="text-2xl font-bold text-blue-600">{results.usersUpdated}</p>
                  </div>
                  <div className="text-center">
                    <XCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Skipped</p>
                    <p className="text-2xl font-bold text-gray-400">{results.skipped}</p>
                  </div>
                </div>

                {results.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                    <div className="bg-red-50 p-3 rounded">
                      {results.errors.map((error, index) => (
                        <p key={index} className="text-sm text-red-700">
                          <strong>{error.user}:</strong> {error.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Users Without Accounts */}
            {usersWithoutAccounts.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Users Without Accounts</h3>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {usersWithoutAccounts.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {user.name || user.displayName || 'No name'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {user.role || 'No role'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.status === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.status || 'Unknown'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
